import { api } from './axios';
import { filesApi } from './files';

const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * API для работы со стикерпаками и стикерами.
 *
 * Изображения стикеров загружаются через стандартный файловый модуль
 * (POST /api/files/upload/image или /api/files/upload/batch),
 * а модуль стикерпаков лишь связывает эти файлы в именованные наборы
 * по imageFileId.
 *
 * Эндпоинты (Auth — требуется авторизация, ADMIN — роль администратора):
 *   GET    /api/sticker-packs                        — список паков (пагинация)
 *   GET    /api/sticker-packs/search?query=          — поиск паков
 *   GET    /api/sticker-packs/{id}                   — детали пака со стикерами
 *   GET    /api/sticker-packs/{id}/stickers          — стикеры пака
 *   POST   /api/sticker-packs                        — создать пак (ADMIN)
 *   PUT    /api/sticker-packs/{id}                   — обновить пак (ADMIN)
 *   DELETE /api/sticker-packs/{id}                   — удалить пак (ADMIN)
 *   POST   /api/sticker-packs/{id}/stickers          — добавить стикер (ADMIN)
 *   PUT    /api/sticker-packs/{id}/stickers/{sid}    — обновить стикер (ADMIN)
 *   DELETE /api/sticker-packs/{id}/stickers/{sid}    — удалить стикер (ADMIN)
 *
 * StickerPackResponse: { id, name, iconFileId?, sortOrder, isActive, stickers[] }
 * StickerResponse:     { id, imageFileId, emoji?, sortOrder, isActive }
 */
export const stickerPacksApi = {
    // ====================================================================
    //  Публичные эндпоинты (Auth)
    // ====================================================================

    /**
     * Получить список стикерпаков с пагинацией.
     * GET /api/sticker-packs
     * @param {Object} [params] - Параметры запроса
     * @param {number} [params.page=0] - Номер страницы (0-indexed)
     * @param {number} [params.size=20] - Размер страницы
     * @param {string} [params.sortBy] - Поле сортировки
     * @param {string} [params.direction=ASC] - Направление (ASC / DESC)
     * @returns {Promise<Object|Array>} Page-объект ({ content, totalElements, totalPages, ... })
     *         либо плоский массив (если сервер вернул List)
     */
    getStickerPacks: async (params = {}) => {
        try {
            const { data } = await api.get('/sticker-packs', { params });
            return data;
        } catch (error) {
            console.error('[stickerPacksApi.getStickerPacks] Failed to fetch sticker packs:', error.message);
            throw error;
        }
    },

    /**
     * Получить список стикерпаков в виде плоского массива.
     * Запрашивает большую страницу (size=1000) и возвращает content.
     * Удобно для использования в StickerPicker, где пагинация не нужна.
     * @returns {Promise<Array>} Массив StickerPackResponse[]
     */
    getStickerPacksAll: async () => {
        try {
            const { data } = await api.get('/sticker-packs', { params: { size: 1000 } });
            if (Array.isArray(data)) return data;
            if (data && Array.isArray(data.content)) return data.content;
            return [];
        } catch (error) {
            console.error('[stickerPacksApi.getStickerPacksAll] Failed to fetch all sticker packs:', error.message);
            throw error;
        }
    },

    /**
     * Поиск стикерпаков по названию.
     * GET /api/sticker-packs/search?query=
     * @param {string} query - Поисковый запрос
     * @param {Object} [params] - Дополнительные параметры пагинации
     * @param {number} [params.page=0]
     * @param {number} [params.size=20]
     * @returns {Promise<Object|Array>} Результаты поиска (Page или массив)
     */
    searchStickerPacks: async (query, params = {}) => {
        if (!query || !query.trim()) return { content: [] };
        try {
            const { data } = await api.get('/sticker-packs/search', {
                params: { query: query.trim(), ...params }
            });
            return data;
        } catch (error) {
            console.error('[stickerPacksApi.searchStickerPacks] Search failed:', error.message);
            throw error;
        }
    },

    /**
     * Получить детали стикерпака со стикерами.
     * GET /api/sticker-packs/{id}
     * @param {string} packId - ID стикерпака (UUID)
     * @returns {Promise<Object>} StickerPackResponse
     *   ({ id, name, iconFileId?, sortOrder, isActive, stickers[] })
     */
    getStickerPack: async (packId) => {
        validateId(packId, 'packId');
        try {
            const { data } = await api.get(`/sticker-packs/${packId}`);
            return data;
        } catch (error) {
            console.error(`[stickerPacksApi.getStickerPack] Failed to fetch sticker pack ${packId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить стикеры конкретного пака (без метаданных пака).
     * GET /api/sticker-packs/{id}/stickers
     * @param {string} packId - ID стикерпака (UUID)
     * @returns {Promise<Array>} Массив StickerResponse[]
     *   ({ id, imageFileId, emoji?, sortOrder, isActive })
     */
    getStickerPackStickers: async (packId) => {
        validateId(packId, 'packId');
        try {
            const { data } = await api.get(`/sticker-packs/${packId}/stickers`);
            return Array.isArray(data) ? data : (data?.content || []);
        } catch (error) {
            console.error(`[stickerPacksApi.getStickerPackStickers] Failed to fetch stickers for pack ${packId}:`, error.message);
            throw error;
        }
    },

    // ====================================================================
    //  Административные эндпоинты (ADMIN)
    // ====================================================================

    /**
     * Создать стикерпак.
     * POST /api/sticker-packs
     * @param {Object} payload - Данные пака
     * @param {string} payload.name - Название пака
     * @param {string} [payload.iconFileId] - ID файла-иконки пака (UUID из files API)
     * @param {number} [payload.sortOrder=0] - Порядок сортировки
     * @param {boolean} [payload.isActive=true] - Активен ли пак
     * @returns {Promise<Object>} Созданный StickerPackResponse
     */
    createStickerPack: async (payload) => {
        if (!payload) throw new Error('Payload is required');
        if (!payload.name) throw new Error('name is required');
        try {
            const { data } = await api.post('/sticker-packs', payload);
            return data;
        } catch (error) {
            console.error('[stickerPacksApi.createStickerPack] Failed to create sticker pack:', error.message);
            throw error;
        }
    },

    /**
     * Обновить стикерпак.
     * PUT /api/sticker-packs/{id}
     * @param {string} packId - ID стикерпака (UUID)
     * @param {Object} payload - Обновляемые поля
     * @param {string} [payload.name] - Новое название
     * @param {string} [payload.iconFileId] - Новый ID файла-иконки
     * @param {number} [payload.sortOrder] - Новый порядок сортировки
     * @param {boolean} [payload.isActive] - Активность пака
     * @returns {Promise<Object>} Обновлённый StickerPackResponse
     */
    updateStickerPack: async (packId, payload) => {
        validateId(packId, 'packId');
        if (!payload) throw new Error('Payload is required');
        try {
            const { data } = await api.put(`/sticker-packs/${packId}`, payload);
            return data;
        } catch (error) {
            console.error(`[stickerPacksApi.updateStickerPack] Failed to update sticker pack ${packId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить стикерпак.
     * DELETE /api/sticker-packs/{id}
     * @param {string} packId - ID стикерпака (UUID)
     * @returns {Promise<void>}
     */
    deleteStickerPack: async (packId) => {
        validateId(packId, 'packId');
        try {
            await api.delete(`/sticker-packs/${packId}`);
        } catch (error) {
            console.error(`[stickerPacksApi.deleteStickerPack] Failed to delete sticker pack ${packId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавить стикер в пак.
     * POST /api/sticker-packs/{id}/stickers
     * @param {string} packId - ID стикерпака (UUID)
     * @param {Object} payload - Данные стикера
     * @param {string} payload.imageFileId - ID файла-изображения (UUID из files API)
     * @param {string} [payload.emoji] - Эмодзи-метка для стикера
     * @param {number} [payload.sortOrder=0] - Порядок сортировки
     * @param {boolean} [payload.isActive=true] - Активен ли стикер
     * @returns {Promise<Object>} Созданный StickerResponse
     */
    addSticker: async (packId, payload) => {
        validateId(packId, 'packId');
        if (!payload) throw new Error('Payload is required');
        if (!payload.imageFileId) throw new Error('imageFileId is required');
        try {
            const { data } = await api.post(`/sticker-packs/${packId}/stickers`, payload);
            return data;
        } catch (error) {
            console.error(`[stickerPacksApi.addSticker] Failed to add sticker to pack ${packId}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить стикер в паке.
     * PUT /api/sticker-packs/{id}/stickers/{stickerId}
     * @param {string} packId - ID стикерпака (UUID)
     * @param {string} stickerId - ID стикера (UUID)
     * @param {Object} payload - Обновляемые поля
     * @param {string} [payload.imageFileId] - Новый ID файла
     * @param {string} [payload.emoji] - Новый эмодзи
     * @param {number} [payload.sortOrder] - Новый порядок сортировки
     * @param {boolean} [payload.isActive] - Активность стикера
     * @returns {Promise<Object>} Обновлённый StickerResponse
     */
    updateSticker: async (packId, stickerId, payload) => {
        validateId(packId, 'packId');
        validateId(stickerId, 'stickerId');
        if (!payload) throw new Error('Payload is required');
        try {
            const { data } = await api.put(`/sticker-packs/${packId}/stickers/${stickerId}`, payload);
            return data;
        } catch (error) {
            console.error(`[stickerPacksApi.updateSticker] Failed to update sticker ${stickerId} in pack ${packId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить стикер из пака.
     * DELETE /api/sticker-packs/{id}/stickers/{stickerId}
     * @param {string} packId - ID стикерпака (UUID)
     * @param {string} stickerId - ID стикера (UUID)
     * @returns {Promise<void>}
     */
    deleteSticker: async (packId, stickerId) => {
        validateId(packId, 'packId');
        validateId(stickerId, 'stickerId');
        try {
            await api.delete(`/sticker-packs/${packId}/stickers/${stickerId}`);
        } catch (error) {
            console.error(`[stickerPacksApi.deleteSticker] Failed to delete sticker ${stickerId} from pack ${packId}:`, error.message);
            throw error;
        }
    },

    // ====================================================================
    //  Convenience-методы: полный цикл создания пака
    // ====================================================================

    /**
     * Полный цикл создания стикерпака из массива файлов изображений.
     *
     * Двухшаговый процесс:
     * 1. Загрузка изображений через files API (batch, до 10 за раз)
     * 2. Создание пака + добавление стикеров по imageFileId
     *
     * @param {Object} options
     * @param {string} options.name - Название пака
     * @param {File} [options.iconFile] - Файл-иконка пака (опционально)
     * @param {File[]} options.stickerFiles - Файлы изображений стикеров
     * @param {string[]} [options.emojis] - Эмодзи для стикеров (по индексу)
     * @param {number} [options.sortOrder=0] - sortOrder пака
     * @param {Function} [options.onProgress] - Колбэк прогресса (step, completed, total)
     * @returns {Promise<Object>} Созданный StickerPackResponse со стикерами
     */
    createPackFromFiles: async ({ name, iconFile, stickerFiles, emojis = [], sortOrder = 0, onProgress }) => {
        if (!name) throw new Error('name is required');
        if (!stickerFiles || stickerFiles.length === 0) throw new Error('stickerFiles is required');

        const total = stickerFiles.length + (iconFile ? 1 : 0) + 1; // uploads + create pack
        let completed = 0;

        // --- Шаг 1: Загрузка иконки пака ---
        let iconFileId = null;
        if (iconFile) {
            onProgress?.({ step: 'upload-icon', completed, total });
            const iconRes = await filesApi.uploadImage(iconFile, { entityType: 'CHAT' });
            iconFileId = iconRes?.file?.id || iconRes?.id;
            if (!iconFileId) throw new Error('Failed to upload pack icon: no file ID');
            completed++;
        }

        // --- Шаг 2: Загрузка стикеров (батчами до 10) ---
        const stickerFileIds = [];
        for (let i = 0; i < stickerFiles.length; i += 10) {
            const batch = stickerFiles.slice(i, i + 10);
            onProgress?.({ step: 'upload-stickers', completed, total, batchIndex: Math.floor(i / 10) });
            const batchRes = await filesApi.uploadBatch(batch, { entityType: 'CHAT' });
            const batchFiles = batchRes?.files || [];
            for (const f of batchFiles) {
                const fid = f?.id;
                if (fid) stickerFileIds.push(fid);
            }
            completed += batch.length;
        }

        if (stickerFileIds.length === 0) throw new Error('Failed to upload sticker images: no file IDs');

        // --- Шаг 3: Создание пака ---
        onProgress?.({ step: 'create-pack', completed, total });
        const pack = await stickerPacksApi.createStickerPack({
            name,
            iconFileId,
            sortOrder,
            isActive: true,
        });
        completed++;

        // --- Шаг 4: Добавление стикеров в пак ---
        const stickers = [];
        for (let i = 0; i < stickerFileIds.length; i++) {
            onProgress?.({ step: 'add-stickers', completed, total, stickerIndex: i });
            const sticker = await stickerPacksApi.addSticker(pack.id, {
                imageFileId: stickerFileIds[i],
                emoji: emojis[i] || null,
                sortOrder: i,
                isActive: true,
            });
            stickers.push(sticker);
        }

        // Возвращаем пак со стикерами
        return { ...pack, stickers };
    },
};
